import {ChangeDetectorRef, Component, OnInit, Optional} from '@angular/core';
import {LayoutService} from '@ngmedax/layout';
import {MediaCenterService} from '@ngmedax/asset';
import {ValueService} from '@ngmedax/value';
import {DATE_FORMAT_YMD, DATE_FORMAT_YMD_HM, DateFormatService, Translatable, TranslationService} from '@ngmedax/translation';
import {SubmissionService} from '../../services/submission.service';
import {Submission} from '../../../../types';
import {TRANSLATION_GRID_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';


// hack to inject decorator declarations. must occur before class declaration!
export interface QuestionnaireSubmissionGridComponent extends Translatable {}

@Component({
  selector: 'app-questionnaire-submission-grid',
  templateUrl: './questionnaire-submission-grid.component.html',
  styleUrls: ['./questionnaire-submission-grid.component.css']
})
@Translatable({scope: TRANSLATION_GRID_SCOPE, keys: KEYS})
export class QuestionnaireSubmissionGridComponent implements OnInit {
  public isSearchCollapsed = true;
  public isGridCollapsed = false;
  public gridPageNumber = 1;
  public displayPerPage = 25;
  public submissions = [];
  public filter: Submission.Filter = {};
  public clientFilter: {[key: string]: any} = {};
  public customFilter: {[key: string]: any} = {};
  public total = 0;

  /**
   * Locale for patients. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  /**
   * Timeout for filter change event
   */
  private filterChangeTimeout: any = null;

  /**
   * Timeout for show preloader event
   */
  private showPreloaderTimeout: any = null;

  /**
   * date format to use in grid
   * @type {string}
   */
  public get dateFormat() {
    const format = DATE_FORMAT_YMD_HM.replace(/YYYY/, 'YY');
    return this.getDateFormat(format);
  };

  /**
   * Can we use the asset module?
   * @type {boolean}
   */
  public hasAssetSupport = false;

  /**
   * Injects dependencies
   */
  public constructor(
    private layoutService: LayoutService,
    private submissionService: SubmissionService,
    private ref: ChangeDetectorRef,
    private value: ValueService,
    @Optional() private mediaCenterService: MediaCenterService,
    @Optional() private translationService: TranslationService,
    @Optional() private dateFormatService: DateFormatService
  ) {
    this.hasAssetSupport = !!this.mediaCenterService;
  }

  /**
   * Loads patients and initializes search filter
   */
  public ngOnInit() {
    this.layoutService.showPreloader();
    this.loadSubmissions();
  }

  /**
   * Refreshes the submission grid data
   */
  public async onRefresh() {
    this.showPreloader();
    await new Promise(resolve => setTimeout(resolve, 500));
    this.loadSubmissions();
  }

  /**
   * Event handler for when paging changes. Triggers the change detection
   */
  public onPagingChange() {
    // trigger change detection
    this.ref.detectChanges();
    this.ref.markForCheck();

    this.loadSubmissions();
  }

  /**
   * Event handler for when filter changes. Loads patients
   */
  public onFilterChange() {
    clearTimeout(this.filterChangeTimeout);
    this.filterChangeTimeout = setTimeout(() => this.loadSubmissions(), 1000);
  }

  /**
   * Downloads export file by given file name
   *
   * @param exportFileName
   */
  public onDownload(exportFileName: string) {
    const exportLink = this.submissionService.getDownloadLink(exportFileName);
    window.open(exportLink, '_blank');
  }

  /**
   * Opens media center modal for submission assets
   *
   * @param {Submission} submission
   */
  public onOpenMediaCenterModal(submission: Submission) {
    this.mediaCenterService && this.mediaCenterService.openModal(
      {bucketId: submission.bucketId, disableUpload: true, enableZipDownload: true});
  }

  /**
   * Opens given link
   *
   * @param {string} link
   */
  public onOpenLink(link: string) {
    link && window.open(link, '_blank');
  }

  /**
   * Converts mysql date to local ymd date
   *
   * @param {string} date
   * @returns {string}
   */
  public fromMySQLDate(date): string {
    return this.dateFormatService && date ? this.dateFormatService.fromMySqlDate(date, this.getDateFormat(DATE_FORMAT_YMD)) : date;
  }

  /**
   * Returns true if submission has valid exports
   * @param exports
   */
  public hasValidExports(submission: Submission) {
    const exports = submission.exports;
    const isArray = Array.isArray(exports);
    const hasElements = isArray && exports.length > 0;
    const hasObjects = hasElements && typeof exports[0] === 'object';
    const hasExpectedFormat = hasObjects && !!exports[0].type && !!exports[0].fileName;

    return hasExpectedFormat;
  }

  /**
   * Returns submissions which are allowed by currently set filter
   *
   * @returns {Array}
   */
  public getSubmissions(): Submission[] {
    return this.submissions;
  }

  /**
   * Loads submissions
   */
  private loadSubmissions() {
    this.showPreloader();

    const filter = this.getQueryFilter();
    const opts: any = this.getQueryOpts();

    this.submissionService.getSubmissions(filter, opts)
      .then((result) => {
        this.submissions = result.rows;
        this.total = result.total;
        this.hidePreloader();
      })
      .catch(error => {
        this.hidePreloader();
        alert(this._(KEYS.GRID.ERROR_LOADING_QUESTIONNAIRES));
        console.log(error);
      });
  }

  /**
   * Returns query filter by filter object
   */
  private getQueryFilter() {
    const filter: any = {};
    const like = (value) => {return { '$regex': value, '$options' : 'i' }};

    this.filter.questionnaireTitle && (filter['questionnaire.meta.title.de_DE'] = like(this.filter.questionnaireTitle));
    this.filter._id && (filter['_id'] = like(this.filter._id));

    // standard fields from clientFilter
    for (const key of Object.keys(this.clientFilter)) {
      if (this.clientFilter[key]) {
        filter[`client.${key}`] = like(this.clientFilter[key]);
      }
    }

    // custom fields from customFilter
    for (const key of Object.keys(this.customFilter)) {
      if (this.customFilter[key]) {
        filter[`client.custom.${key}`] = like(this.customFilter[key]);
      }
    }

    return filter;
  }

  /**
   * Returns query opts
   */
  private getQueryOpts() {
    return {limit: this.displayPerPage, offset: (this.gridPageNumber - 1) * this.displayPerPage}
  }

  /**
   * Shows preloader after 200ms
   */
  private showPreloader() {
    this.showPreloaderTimeout = setTimeout(() => this.layoutService.showPreloader(), 200);
  }

  /**
   * Hides preloader
   */
  private hidePreloader() {
    clearTimeout(this.showPreloaderTimeout);
    this.layoutService.hidePreloader();
    setTimeout(() => this.layoutService.hidePreloader(), 1000);
  }
}
